import React, { Suspense } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownRenderer = ({ content, className = "" }) => {
  if (!content) return null;

  return (
    <Suspense fallback={<div className="text-gray-500">Loading...</div>}>
      <div className={`prose dark:prose-invert max-w-none ${className}`}>
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            code({node, inline, className, children, ...props}) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <div className="relative">
                  <pre className={`language-${match[1]} rounded overflow-auto p-4 bg-gray-100 dark:bg-gray-800`}>
                    <code {...props} className={`language-${match[1]}`}>
                      {String(children).replace(/\n$/, '')}
                    </code>
                  </pre>
                </div>
              ) : (
                <code {...props} className={`${className} bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm`}>
                  {children}
                </code>
              );
            },
            blockquote({children}) {
              return (
                <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300">
                  {children}
                </blockquote>
              );
            },
            table({children}) {
              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-300 dark:border-gray-600">
                    {children}
                  </table>
                </div>
              );
            },
            th({children}) {
              return (
                <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 bg-gray-100 dark:bg-gray-800 font-semibold text-left">
                  {children}
                </th>
              );
            },
            td({children}) {
              return (
                <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                  {children}
                </td>
              );
            },
            h1({children}) {
              return <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{children}</h1>;
            },
            h2({children}) {
              return <h2 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">{children}</h2>;
            },
            h3({children}) {
              return <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">{children}</h3>;
            },
            p({children}) {
              return <p className="mb-3 text-gray-700 dark:text-gray-300">{children}</p>;
            },
            ul({children}) {
              return <ul className="list-disc list-inside mb-3 text-gray-700 dark:text-gray-300">{children}</ul>;
            },
            ol({children}) {
              return <ol className="list-decimal list-inside mb-3 text-gray-700 dark:text-gray-300">{children}</ol>;
            },
            li({children}) {
              return <li className="mb-1">{children}</li>;
            },
            strong({children}) {
              return <strong className="font-bold text-gray-900 dark:text-white">{children}</strong>;
            },
            em({children}) {
              return <em className="italic text-gray-700 dark:text-gray-300">{children}</em>;
            },
            a({href, children}) {
              return (
                <a 
                  href={href} 
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              );
            }
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </Suspense>
  );
};

export default MarkdownRenderer;